import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { inr, apiErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DashShell } from "@/components/dash";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import PrintJobChat from "@/components/PrintJobChat";
import { logTermsCheckpoint } from "@/lib/termsCheckpoint";
import {
  ClipboardList, Package, Wrench, Printer, Wallet, CreditCard,
  CheckCircle2, XCircle, Loader2, Phone, Mail, MapPin, Truck, HandCoins,
  Download, ArrowRight, IndianRupee, Play, PackageCheck, Clock,
  Save, PowerOff, ToggleLeft, ToggleRight,
} from "lucide-react";

const MACHINE_LABEL = { sla: "SLA", dlp: "DLP", msla: "MSLA", wax_jet: "Wax-Jet", fdm: "FDM" };

const STATUS_STYLE = {
  new:       "bg-blue-500/15 text-blue-500 border-blue-500/30",
  accepted:  "bg-indigo-500/15 text-indigo-500 border-indigo-500/30",
  quoted:    "bg-amber-500/15 text-amber-500 border-amber-500/30",
  confirmed: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  printing:  "bg-purple-500/15 text-purple-500 border-purple-500/30",
  ready:     "bg-teal-500/15 text-teal-500 border-teal-500/30",
  shipped:   "bg-sky-500/15 text-sky-500 border-sky-500/30",
  completed: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  declined:  "bg-red-500/15 text-red-500 border-red-500/30",
  cancelled: "bg-gray-500/15 text-gray-500 border-gray-500/30",
};

const NAV = [
  { key: "jobs", label: "Jobs", icon: ClipboardList },
  { key: "profile", label: "Shop Profile", icon: Wrench },
  { key: "earnings", label: "Earnings", icon: Wallet },
  { key: "subscription", label: "Subscription", icon: CreditCard },
];

const JOB_BUCKETS = [
  { key: "new", label: "New" },
  { key: "active", label: "Active" },
  { key: "shipped", label: "Shipped" },
  { key: "completed", label: "Completed" },
  { key: "declined", label: "Declined" },
];

export default function VendorDashboard() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const active = params.get("tab") || "jobs";
  const setTab = (t) => setParams({ tab: t });

  const isVendor = user?.role === "cam_vendor" || user?.is_cam_vendor;
  if (!user) return <NeedsAuth />;
  if (!isVendor) return <WrongRole userRole={user.role} />;

  return (
    <DashShell
      title="Vendor Studio"
      subtitle={user.vendor_shop_name || user.name}
      nav={NAV}
      active={active}
      onTab={setTab}
      currentRole="cam_vendor"
    >
      {active === "jobs" && <JobsTab />}
      {active === "profile" && <ProfileTab />}
      {active === "earnings" && <EarningsTab />}
      {active === "subscription" && <SubscriptionTab />}
    </DashShell>
  );
}

// ---------- JOBS TAB ----------
function JobsTab() {
  const [bucket, setBucket] = useState("new");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {type:'accept'|'decline'|'quote'|'status', job}

  const load = () => {
    setLoading(true);
    api.get("/vendor/jobs", { params: { bucket } })
      .then((r) => setJobs(r.data))
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [bucket]);

  const closeModal = () => setModal(null);
  const afterMutation = () => { closeModal(); load(); };

  return (
    <div className="space-y-5" data-testid="vendor-jobs-tab">
      <div>
        <h2 className="font-display font-semibold text-lg flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-primary" /> Print Jobs
        </h2>
        <p className="text-xs text-muted-foreground mt-1 max-w-2xl leading-relaxed">
          Every job below is a real STL from a paying buyer. Accept it, enter the actual print cost, and update the status as you print → ship → deliver.
        </p>
      </div>

      {/* bucket tabs */}
      <div className="flex flex-wrap gap-1 border-b border-border" role="tablist">
        {JOB_BUCKETS.map((b) => (
          <button
            key={b.key}
            onClick={() => setBucket(b.key)}
            data-testid={`jobs-bucket-${b.key}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              bucket === b.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {b.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading jobs…
        </div>
      ) : jobs.length === 0 ? (
        <div className="border border-dashed border-border rounded-md py-20 text-center text-sm text-muted-foreground" data-testid="jobs-empty">
          {bucket === "new" ? "No incoming print jobs yet — you'll be notified the moment a buyer sends one your way." : `No ${bucket} jobs.`}
        </div>
      ) : (
        <div className="grid gap-3">
          {jobs.map((j) => (
            <JobCard key={j.id} job={j} onAction={(type) => setModal({ type, job: j })} />
          ))}
        </div>
      )}

      {modal?.type === "accept"   && <AcceptModal   job={modal.job} onDone={afterMutation} onClose={closeModal} />}
      {modal?.type === "decline"  && <DeclineModal  job={modal.job} onDone={afterMutation} onClose={closeModal} />}
      {modal?.type === "quote"    && <QuoteModal    job={modal.job} onDone={afterMutation} onClose={closeModal} />}
      {modal?.type === "status"   && <StatusModal   job={modal.job} onDone={afterMutation} onClose={closeModal} />}
    </div>
  );
}

function JobCard({ job, onAction }) {
  const status = job.status;
  const machine = MACHINE_LABEL[job.machine_requested] || job.machine_requested?.toUpperCase();
  return (
    <div className="border border-border rounded-md bg-card p-4 sm:p-5" data-testid={`job-${job.id}`}>
      <div className="flex flex-col md:flex-row md:items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-display font-bold text-base leading-tight">{job.listing?.title || "Untitled listing"}</p>
            <span className={`text-[10px] font-mono uppercase tracking-widest border rounded-full px-2 py-0.5 ${STATUS_STYLE[status] || ""}`} data-testid={`job-status-${job.id}`}>
              {status}
            </span>
            {job.tracking_number && (
              <span className="text-[10px] font-mono uppercase tracking-widest border rounded-full px-2 py-0.5 border-border">
                <Truck className="inline h-3 w-3 mr-1" /> {job.tracking_number}
              </span>
            )}
          </div>
          <p className="font-mono text-[11px] text-muted-foreground mt-0.5">
            Job #{job.id.slice(0, 8)} · Order {job.order_id?.slice(0, 8)} · Applied {new Date(job.created_at).toLocaleDateString()}
          </p>

          {/* Buyer + logistics */}
          {job.buyer && (
            <div className="mt-3 grid sm:grid-cols-3 gap-2 text-xs">
              <a href={`mailto:${job.buyer.email}`} className="flex items-center gap-2 hover:text-primary min-w-0">
                <Mail className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{job.buyer.name}</span>
              </a>
              {job.buyer.phone && (
                <a href={`tel:${job.buyer.phone}`} className="flex items-center gap-2 hover:text-primary">
                  <Phone className="h-3.5 w-3.5 shrink-0" /> {job.buyer.phone}
                </a>
              )}
              <span className="flex items-center gap-2">
                {job.delivery_mode === "delivery" ? <Truck className="h-3.5 w-3.5" /> : <PackageCheck className="h-3.5 w-3.5" />}
                {job.delivery_mode === "delivery" ? "Delivery" : "Pickup"}
              </span>
              {job.delivery_mode === "delivery" && job.delivery_address && (
                <span className="sm:col-span-3 flex items-center gap-2 text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5 shrink-0" /> {job.delivery_address}
                </span>
              )}
            </div>
          )}

          {/* Money row */}
          <div className="mt-3 flex flex-wrap gap-4 text-xs">
            <span className="flex items-center gap-1.5">
              <span className="text-muted-foreground">Machine:</span>
              <span className="font-semibold">{machine}</span>
            </span>
            {job.listing?.cam_weight != null && (
              <span className="flex items-center gap-1.5">
                <span className="text-muted-foreground">CAM wt:</span>
                <span className="font-mono">{job.listing.cam_weight} g</span>
              </span>
            )}
            {job.approx_estimate != null && !job.vendor_quote && (
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <span>Buyer's rough estimate:</span>
                <span className="font-mono">{inr(job.approx_estimate)}</span>
              </span>
            )}
            {job.vendor_quote != null && (
              <span className="flex items-center gap-1.5">
                <span className="text-muted-foreground">Your quote:</span>
                <span className="font-display font-bold text-primary">{inr(job.vendor_quote)}</span>
              </span>
            )}
          </div>

          {/* STL download — only present once vendor has accepted */}
          {job.stl_url && (
            <a
              href={API + job.stl_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`job-stl-${job.id}`}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
            >
              <Download className="h-3.5 w-3.5" /> Download STL
            </a>
          )}
        </div>

        {/* Action rail */}
        <div className="flex flex-row md:flex-col gap-2 shrink-0 md:min-w-[160px]">
          {status === "new" && (
            <>
              <ActionBtn onClick={() => onAction("accept")} icon={CheckCircle2} label="Accept" tone="primary" testid={`accept-${job.id}`} />
              <ActionBtn onClick={() => onAction("decline")} icon={XCircle} label="Decline" tone="danger" testid={`decline-${job.id}`} />
            </>
          )}
          {status === "accepted" && (
            <ActionBtn onClick={() => onAction("quote")} icon={IndianRupee} label="Enter cost" tone="primary" testid={`quote-${job.id}`} />
          )}
          {status === "quoted" && (
            <span className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/5 text-amber-600 text-xs font-medium px-3 py-2">
              <Clock className="h-3.5 w-3.5" /> Awaiting buyer
            </span>
          )}
          {status === "confirmed" && (
            <ActionBtn onClick={() => onAction("status")} icon={Play} label="Start printing" tone="primary" testid={`start-${job.id}`} />
          )}
          {status === "printing" && (
            <ActionBtn onClick={() => onAction("status")} icon={Package} label="Mark ready" tone="primary" testid={`ready-${job.id}`} />
          )}
          {status === "ready" && (
            <ActionBtn onClick={() => onAction("status")} icon={job.delivery_mode === "delivery" ? Truck : HandCoins} label={job.delivery_mode === "delivery" ? "Mark shipped" : "Mark completed"} tone="primary" testid={`next-${job.id}`} />
          )}
          {status === "shipped" && (
            <ActionBtn onClick={() => onAction("status")} icon={CheckCircle2} label="Mark delivered" tone="primary" testid={`complete-${job.id}`} />
          )}
        </div>
      </div>
      <PrintJobChat jobId={job.id} me="vendor" />
    </div>
  );
}

function ActionBtn({ onClick, icon: Icon, label, tone = "primary", testid }) {
  const cls = tone === "danger"
    ? "border border-red-500/40 text-red-600 hover:bg-red-500/5"
    : "bg-primary text-primary-foreground hover:bg-primary/90";
  return (
    <button onClick={onClick} data-testid={testid} className={`w-full inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold transition-colors ${cls}`}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
}

// ---------- MODALS ----------
function AcceptModal({ job, onDone, onClose }) {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const submit = async () => {
    if (!confirm) return toast.error("Please tick the confirmation to accept this job.");
    setBusy(true);
    try {
      await api.post(`/vendor/jobs/${job.id}/accept`);
      logTermsCheckpoint({
        role: "cam_vendor",
        event: "accept_job",
        contextId: job.id,
        checkboxes: ["vendor_terms", "vendor_file_use"],
      });
      toast.success("Accepted — enter your printing cost next.");
      onDone();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-500" /> Accept this print job?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground leading-relaxed">
          You'll gain access to the STL file and the buyer's contact details.
          The next step is to enter your <strong className="text-foreground">actual printing cost</strong> — the buyer must confirm before you start printing.
        </p>
        <label className="flex items-start gap-2 text-xs border border-amber-500/40 bg-amber-500/10 rounded-md p-3" data-testid="accept-vendor-file-confirm">
          <input
            type="checkbox"
            checked={confirm}
            onChange={(e) => setConfirm(e.target.checked)}
            className="mt-0.5"
            data-testid="accept-vendor-file-confirm-input"
          />
          <span className="leading-snug">
            I confirm I will use the STL file <b>only for this single job</b>, will not copy, retain, reuse, or share it, and will handle the buyer's contact data solely to fulfil this order (per CAM Vendor Terms).
          </span>
        </label>
        <DialogFooter>
          <button onClick={onClose} className="border border-border rounded-md px-4 py-2 text-sm">Cancel</button>
          <button onClick={submit} disabled={busy || !confirm} data-testid="accept-confirm" className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Accept job
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeclineModal({ job, onDone, onClose }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/vendor/jobs/${job.id}/decline`, { reason });
      toast.success("Job declined. The buyer has been notified.");
      onDone();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle className="flex items-center gap-2"><XCircle className="h-5 w-5 text-red-500" /> Decline this print job?</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Let the buyer know briefly why — helps them pick a better vendor next time.</p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="e.g. Wrong machinery for this piece / booked out this week / …"
          data-testid="decline-reason"
          className="mt-2 w-full border border-border rounded-md p-2.5 text-sm bg-background"
        />
        <DialogFooter>
          <button onClick={onClose} className="border border-border rounded-md px-4 py-2 text-sm">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="decline-confirm" className="bg-red-600 text-white rounded-md px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Decline
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuoteModal({ job, onDone, onClose }) {
  const [amount, setAmount] = useState(job.approx_estimate ? String(Math.round(job.approx_estimate)) : "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      await api.post(`/vendor/jobs/${job.id}/quote`, { amount: amt });
      toast.success(`Quote of ${inr(amt)} sent to buyer.`);
      onDone();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle className="flex items-center gap-2"><IndianRupee className="h-5 w-5 text-primary" /> Enter actual printing cost</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Base price for this job — includes material, supports, machine time, and finishing.
          {job.approx_estimate != null && (
            <> Buyer's rough estimate was <strong className="text-foreground">{inr(job.approx_estimate)}</strong>.</>
          )}
        </p>
        <div className="mt-3 relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0"
            data-testid="quote-amount"
            min="1"
            className="w-full border border-border rounded-md pl-7 p-2.5 text-lg font-display font-bold bg-background"
            autoFocus
          />
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">Buyer must confirm before you begin printing. You can adjust status once they've paid.</p>
        <DialogFooter>
          <button onClick={onClose} className="border border-border rounded-md px-4 py-2 text-sm">Cancel</button>
          <button onClick={submit} disabled={busy || !amount} data-testid="quote-confirm" className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Send quote
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatusModal({ job, onDone, onClose }) {
  // Compute the next valid transition based on current status + delivery mode.
  const nextByCurrent = {
    confirmed: [{ key: "printing", label: "Start printing", icon: Play }],
    printing:  [{ key: "ready", label: "Mark ready for handover", icon: Package }],
    ready:     job.delivery_mode === "delivery"
      ? [{ key: "shipped", label: "Mark shipped", icon: Truck }]
      : [{ key: "completed", label: "Buyer collected — mark completed", icon: CheckCircle2 }],
    shipped:   [{ key: "completed", label: "Buyer received — mark completed", icon: CheckCircle2 }],
  };
  const options = nextByCurrent[job.status] || [];
  const [selected, setSelected] = useState(options[0]?.key || "");
  const [tracking, setTracking] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const body = { status: selected };
      if (selected === "shipped" && tracking) body.tracking_number = tracking.trim();
      if (note) body.note = note;
      await api.post(`/vendor/jobs/${job.id}/status`, body);
      toast.success(`Status updated → ${selected}`);
      onDone();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Update job status</DialogTitle></DialogHeader>
        {options.length === 0 ? (
          <p className="text-sm text-muted-foreground">No further transitions available from <strong>{job.status}</strong>.</p>
        ) : (
          <>
            <div className="space-y-2 mt-1">
              {options.map((o) => (
                <label key={o.key} className={`flex items-center gap-3 border rounded-md p-3 cursor-pointer transition-colors ${selected === o.key ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
                  <input type="radio" checked={selected === o.key} onChange={() => setSelected(o.key)} className="accent-primary" data-testid={`status-opt-${o.key}`} />
                  <o.icon className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">{o.label}</span>
                </label>
              ))}
            </div>
            {selected === "shipped" && (
              <div className="mt-3">
                <label className="text-xs font-mono uppercase text-muted-foreground">Courier tracking number (optional)</label>
                <input
                  type="text"
                  value={tracking}
                  onChange={(e) => setTracking(e.target.value)}
                  data-testid="tracking-input"
                  placeholder="e.g. BD123456789"
                  className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background font-mono"
                />
              </div>
            )}
            <div className="mt-3">
              <label className="text-xs font-mono uppercase text-muted-foreground">Note to buyer (optional)</label>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="status-note" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" />
            </div>
          </>
        )}
        <DialogFooter>
          <button onClick={onClose} className="border border-border rounded-md px-4 py-2 text-sm">Cancel</button>
          {options.length > 0 && (
            <button onClick={submit} disabled={busy || !selected} data-testid="status-confirm" className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Update <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- SHOP PROFILE TAB ----------
const MACHINE_OPTIONS = [
  { key: "sla", label: "SLA (Formlabs)" },
  { key: "dlp", label: "DLP (Asiga / EnvisionTEC)" },
  { key: "msla", label: "MSLA (Phrozen / Elegoo)" },
  { key: "wax_jet", label: "Wax-Jet / MJP" },
  { key: "fdm", label: "FDM (prototypes)" },
];

function ProfileTab() {
  const [profile, setProfile] = useState(null);
  const [cities, setCities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    api.get("/vendor/profile").then((r) => { setProfile(r.data); setForm(shapeForm(r.data)); }).catch((e) => toast.error(apiErr(e)));
    api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleMachine = (k) => set("vendor_machines", form.vendor_machines?.includes(k) ? form.vendor_machines.filter((m) => m !== k) : [...(form.vendor_machines || []), k]);

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const payload = {
        vendor_shop_name: form.vendor_shop_name?.trim(),
        vendor_city: form.vendor_city?.trim() || null,
        vendor_machines: form.vendor_machines || [],
        vendor_rate_per_gram: Number(form.vendor_rate_per_gram) || null,
        vendor_active: !!form.vendor_active,
        vendor_delivery_zones: (form.vendor_delivery_zones_text || "").split(",").map((s) => s.trim()).filter(Boolean),
        vendor_phone: form.vendor_phone?.trim() || null,
        vendor_gst: form.vendor_gst?.trim() || null,
      };
      const { data } = await api.put("/vendor/profile", payload);
      setProfile(data.user);
      setForm(shapeForm(data.user));
      toast.success("Shop profile saved.");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const toggleActive = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/vendor/profile", { vendor_active: !form.vendor_active });
      setProfile(data.user);
      setForm(shapeForm(data.user));
      toast.success(data.user.vendor_active ? "Shop is now ACTIVE — accepting new jobs." : "Shop set to INACTIVE — no new jobs will route to you.");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="flex items-center gap-2 text-sm text-muted-foreground py-10 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Loading profile…</div>;

  return (
    <div className="space-y-6" data-testid="vendor-profile-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display font-semibold text-lg flex items-center gap-2"><Wrench className="h-5 w-5 text-primary" /> Shop Profile</h2>
          <p className="text-xs text-muted-foreground mt-1">Details buyers see when picking a print vendor.</p>
        </div>
        <button
          onClick={toggleActive}
          disabled={saving || (!form.vendor_active && (!form.vendor_rate_per_gram || form.vendor_rate_per_gram <= 0))}
          data-testid="profile-toggle-active"
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50 ${form.vendor_active ? "bg-emerald-500/15 text-emerald-600 border border-emerald-500/40" : "bg-secondary text-muted-foreground border border-border"}`}
        >
          {form.vendor_active ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
          {form.vendor_active ? "Active — accepting jobs" : "Inactive — paused"}
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-6 border border-border rounded-md bg-card p-5">
        <Field label="Shop name" value={form.vendor_shop_name || ""} onChange={(v) => set("vendor_shop_name", v)} testid="profile-shop-name" />
        <div>
          <label className="text-sm font-medium">City</label>
          <input
            list="cities-list"
            value={form.vendor_city || ""}
            onChange={(e) => set("vendor_city", e.target.value)}
            data-testid="profile-city"
            className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background"
            placeholder="Mumbai / Delhi / …"
          />
          <datalist id="cities-list">{cities.map((c) => <option key={c} value={c} />)}</datalist>
        </div>
        <Field label="Phone (with country code)" value={form.vendor_phone || ""} onChange={(v) => set("vendor_phone", v)} placeholder="+91 98xxxxxxxx" testid="profile-phone" />
        <Field label="GST / registration (optional)" value={form.vendor_gst || ""} onChange={(v) => set("vendor_gst", v)} testid="profile-gst" />

        <div>
          <label className="text-sm font-medium">Rate per gram (₹)</label>
          <div className="mt-1 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
            <input
              type="number"
              min="1"
              value={form.vendor_rate_per_gram || ""}
              onChange={(e) => set("vendor_rate_per_gram", e.target.value)}
              placeholder="e.g. 45"
              data-testid="profile-rate"
              className="w-full border border-border rounded-md pl-7 p-2.5 text-sm bg-background font-mono"
            />
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">Buyers see this multiplied by the design's CAM weight as an approximate cost.</p>
        </div>

        <div>
          <label className="text-sm font-medium">Delivery zones (comma-separated)</label>
          <input
            type="text"
            value={form.vendor_delivery_zones_text || ""}
            onChange={(e) => set("vendor_delivery_zones_text", e.target.value)}
            placeholder="e.g. Delhi NCR, Gurgaon, Noida"
            data-testid="profile-zones"
            className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background"
          />
        </div>

        <div className="md:col-span-2">
          <label className="text-sm font-medium">Machinery owned</label>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {MACHINE_OPTIONS.map((m) => (
              <label key={m.key} className={`flex items-start gap-2 text-xs cursor-pointer border rounded-md p-2.5 transition-colors ${form.vendor_machines?.includes(m.key) ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`} data-testid={`profile-machine-${m.key}`}>
                <input type="checkbox" checked={!!form.vendor_machines?.includes(m.key)} onChange={() => toggleMachine(m.key)} className="mt-0.5 accent-primary" />
                <span className="leading-snug">{m.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} data-testid="profile-save" className="bg-primary text-primary-foreground rounded-md px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save profile
        </button>
      </div>
    </div>
  );
}
function shapeForm(u) {
  return {
    vendor_shop_name: u.vendor_shop_name || u.name || "",
    vendor_city: u.vendor_city || "",
    vendor_machines: u.vendor_machines || [],
    vendor_rate_per_gram: u.vendor_rate_per_gram || "",
    vendor_active: !!u.vendor_active,
    vendor_delivery_zones_text: (u.vendor_delivery_zones || []).join(", "),
    vendor_phone: u.vendor_phone || "",
    vendor_gst: u.vendor_gst || "",
  };
}

function Field({ label, value, onChange, placeholder, testid, type = "text" }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background"
      />
    </div>
  );
}

// ---------- EARNINGS TAB ----------
function EarningsTab() {
  const [data, setData] = useState(null);
  const [wdOpen, setWdOpen] = useState(false);

  const load = () => api.get("/vendor/earnings").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(load, []);

  if (!data) return <div className="flex items-center gap-2 text-sm text-muted-foreground py-10 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Loading earnings…</div>;

  const w = data.wallet;
  return (
    <div className="space-y-6" data-testid="vendor-earnings-tab">
      <div>
        <h2 className="font-display font-semibold text-lg flex items-center gap-2"><Wallet className="h-5 w-5 text-primary" /> Earnings & Payouts</h2>
        <p className="text-xs text-muted-foreground mt-1">Buyer pays &rarr; 20% platform margin &rarr; ~2% gateway fee &rarr; your net into escrow for 48h before release.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <WalletCard label="Pending" value={w.pending} sub="Held in escrow (48h)" tone="amber" testid="wallet-pending" />
        <WalletCard label="Available" value={w.available} sub="Ready to withdraw" tone="emerald" testid="wallet-available" />
        <WalletCard label="On hold — dispute" value={w.disputed} sub="Frozen pending admin" tone="red" testid="wallet-disputed" />
        <WalletCard label="Lifetime earned" value={w.total_earned} sub="After margin & fees" tone="primary" testid="wallet-total" />
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => setWdOpen(true)}
          disabled={w.available <= 0}
          data-testid="request-withdrawal"
          className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40"
        >
          <HandCoins className="h-4 w-4" /> Request withdrawal
        </button>
      </div>

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <div className="p-4 border-b border-border">
          <p className="text-sm font-semibold">Job-wise settlement</p>
          <p className="text-[11px] text-muted-foreground">Every row is one paid print job. Numbers already reflect margin + gateway.</p>
        </div>
        {data.rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">No paid jobs yet — settlements appear here as buyers pay.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-left">
              <tr>
                {["Job", "Confirmed", "Buyer paid", "Margin", "Gateway", "Net", "Status"].map((h) => (
                  <th key={h} className="px-3 py-2 text-[11px] uppercase font-mono text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.job_id} className="border-t border-border" data-testid={`settle-row-${r.job_id}`}>
                  <td className="px-3 py-2.5">
                    <div className="text-xs font-semibold">{r.listing_title || "Print job"}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">#{r.job_id.slice(0, 8)}</div>
                  </td>
                  <td className="px-3 py-2.5 text-xs font-mono">{r.confirmed_at ? new Date(r.confirmed_at).toLocaleDateString() : "—"}</td>
                  <td className="px-3 py-2.5 font-mono">{inr(r.buyer_paid)}</td>
                  <td className="px-3 py-2.5 font-mono text-muted-foreground">-{inr(r.platform_margin)}</td>
                  <td className="px-3 py-2.5 font-mono text-muted-foreground">-{inr(r.gateway_fee)}</td>
                  <td className="px-3 py-2.5 font-display font-bold text-primary">{inr(r.vendor_net)}</td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[10px] font-mono uppercase tracking-widest border rounded-full px-2 py-0.5 ${r.bucket === "available" ? "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" : r.bucket === "disputed" ? "bg-red-500/15 text-red-500 border-red-500/30" : "bg-amber-500/15 text-amber-600 border-amber-500/30"}`}>
                      {r.bucket}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {wdOpen && <WithdrawalModal available={w.available} onDone={() => { setWdOpen(false); load(); }} onClose={() => setWdOpen(false)} />}
    </div>
  );
}

function WalletCard({ label, value, sub, tone, testid }) {
  const tones = {
    amber:   "border-amber-500/40 bg-amber-500/5",
    emerald: "border-emerald-500/40 bg-emerald-500/5",
    red:     "border-red-500/40 bg-red-500/5",
    primary: "border-primary/40 bg-primary/5",
  };
  return (
    <div className={`rounded-md border ${tones[tone] || ""} p-4`} data-testid={testid}>
      <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">{label}</p>
      <p className="mt-1 font-display font-extrabold text-2xl">{inr(value || 0)}</p>
      <p className="text-[10px] text-muted-foreground mt-1">{sub}</p>
    </div>
  );
}

function WithdrawalModal({ available, onDone, onClose }) {
  const [amount, setAmount] = useState(available);
  const [method, setMethod] = useState("upi");
  const [dest, setDest] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (!dest.trim()) return toast.error("Add UPI id or bank details");
    setBusy(true);
    try {
      await api.post("/vendor/withdrawals", { amount: amt, method, destination: dest });
      toast.success("Withdrawal requested — our finance team will process it shortly.");
      onDone();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle className="flex items-center gap-2"><HandCoins className="h-5 w-5 text-primary" /> Request withdrawal</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Available: <strong className="text-foreground">{inr(available)}</strong></p>
        <div className="mt-3 space-y-3">
          <div>
            <label className="text-xs font-mono uppercase text-muted-foreground">Amount (₹)</label>
            <input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="wd-amount" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background font-mono" />
          </div>
          <div>
            <label className="text-xs font-mono uppercase text-muted-foreground">Method</label>
            <div className="mt-1 flex gap-2">
              {["upi", "bank"].map((m) => (
                <button key={m} onClick={() => setMethod(m)} data-testid={`wd-method-${m}`} className={`flex-1 rounded-md border p-2 text-sm ${method === m ? "border-primary bg-primary/5" : "border-border"}`}>
                  {m.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-mono uppercase text-muted-foreground">{method === "upi" ? "UPI id" : "Bank details"}</label>
            <textarea rows={2} value={dest} onChange={(e) => setDest(e.target.value)} data-testid="wd-dest" placeholder={method === "upi" ? "yourname@upi" : "Bank name, IFSC, account #"} className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="border border-border rounded-md px-4 py-2 text-sm">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="wd-submit" className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Request
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- PLACEHOLDER TABS + AUTH GUARDS ----------
function PlaceholderTab({ title, body }) {
  return (
    <div className="border border-dashed border-border rounded-md py-16 px-6 text-center" data-testid={`placeholder-${title.toLowerCase().replace(/[^a-z]+/g,"-")}`}>
      <Clock className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
      <h3 className="font-display font-semibold text-lg">{title}</h3>
      <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto leading-relaxed">{body}</p>
    </div>
  );
}

function NeedsAuth() {
  return (
    <div className="max-w-md mx-auto px-6 py-24 text-center">
      <h1 className="font-display font-bold text-xl">Sign in required</h1>
      <p className="text-sm text-muted-foreground mt-2">Use the magic link an admin sent you.</p>
    </div>
  );
}

function WrongRole({ userRole }) {
  return (
    <div className="max-w-md mx-auto px-6 py-24 text-center">
      <h1 className="font-display font-bold text-xl">Not a vendor account</h1>
      <p className="text-sm text-muted-foreground mt-2">You're signed in as <strong className="text-foreground">{userRole}</strong>. Ask an admin to upgrade your account.</p>
    </div>
  );
}


// --- Vendor Subscription tab: 5-tier picker with Razorpay mandate flow ---
function SubscriptionTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);   // tier key currently being subscribed to
  const [cancelling, setCancelling] = useState(false);

  const load = () => api.get("/subscriptions/tiers").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const subscribe = async (tier) => {
    setBusy(tier.key);
    try {
      const { data: r } = await api.post("/subscriptions/subscribe", { tier: tier.key });
      // Open the Razorpay mandate authorization checkout
      const options = {
        key: r.razorpay_key_id,
        subscription_id: r.subscription_id,
        name: "Jeweltix",
        description: `${tier.name} plan — ${data.trial_days}-day free trial`,
        theme: { color: "#2563EB" },
        handler: async () => {
          toast.success(`Mandate authorised — you're on the ${tier.name} plan. First charge in ${data.trial_days} days.`);
          load();
        },
        modal: {
          ondismiss: () => toast.info("You closed the checkout before authorising. Try again anytime."),
        },
      };
      if (!window.Razorpay) {
        // Load the Razorpay script if it's not already on the page
        await new Promise((res, rej) => {
          const s = document.createElement("script");
          s.src = "https://checkout.razorpay.com/v1/checkout.js";
          s.onload = res; s.onerror = rej;
          document.body.appendChild(s);
        });
      }
      new window.Razorpay(options).open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start subscription");
    } finally {
      setBusy(null);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription? You'll keep access until the end of the current billing period.")) return;
    setCancelling(true);
    try {
      const { data: r } = await api.post("/subscriptions/cancel");
      toast.success(r.message || "Cancelled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    } finally {
      setCancelling(false);
    }
  };

  if (!data) return <div className="py-8 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading tiers…</div>;

  return (
    <div className="space-y-5" data-testid="vendor-subscription-tab">
      {/* Banner: current status */}
      {!data.enabled && (
        <div className="border border-amber-500/30 bg-amber-500/10 rounded-md p-4 flex items-start gap-3">
          <Clock className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold text-sm">Free launch period · billing not active yet</p>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">Onboarding is currently free. Our team will notify you at least 15 days before subscriptions go live. You can browse tiers below.</p>
          </div>
        </div>
      )}
      {data.enabled && data.current_tier && (
        <div className="border border-emerald-500/30 bg-emerald-500/10 rounded-md p-4 flex flex-wrap items-center gap-3" data-testid="subscription-active-banner">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">You're on the <strong className="capitalize">{data.current_tier}</strong> plan · Status: <span className="capitalize">{data.current_status}</span></p>
            {data.next_billing_at && <p className="text-xs text-muted-foreground mt-1">Next billing on {new Date(data.next_billing_at).toLocaleDateString()}</p>}
          </div>
          <button onClick={cancel} disabled={cancelling} data-testid="subscription-cancel" className="text-xs border border-red-500/30 text-red-600 rounded-md px-3 py-1.5 hover:bg-red-500/10 disabled:opacity-60">
            {cancelling ? "Cancelling…" : "Cancel plan"}
          </button>
        </div>
      )}

      {/* Tier grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.tiers.map((t, idx) => {
          const isCurrent = data.current_tier === t.key;
          const highlight = t.key === "pro";      // visual anchor
          return (
            <div
              key={t.key}
              data-testid={`tier-${t.key}`}
              className={`relative border rounded-lg bg-card p-5 flex flex-col ${
                isCurrent ? "border-emerald-500/60 ring-2 ring-emerald-500/20" :
                highlight ? "border-primary shadow-md" : "border-border"
              }`}
            >
              {highlight && !isCurrent && (
                <span className="absolute -top-3 left-4 bg-primary text-primary-foreground text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded">Most popular</span>
              )}
              <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Tier {idx + 1}</p>
              <h3 className="font-display font-bold text-xl mt-1">{t.name}</h3>
              <div className="mt-3">
                <span className="font-display font-extrabold text-3xl">{inr(t.price)}</span>
                <span className="text-xs text-muted-foreground font-mono">/month</span>
              </div>
              <ul className="mt-4 space-y-1.5 text-xs text-muted-foreground flex-1">
                {t.features.map((f, i) => (
                  <li key={i} className="flex gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" /> {f}</li>
                ))}
              </ul>
              {isCurrent ? (
                <button disabled className="mt-4 border border-emerald-500/40 text-emerald-600 rounded-md py-2 text-xs font-semibold">Current plan</button>
              ) : (
                <button
                  onClick={() => subscribe(t)}
                  disabled={busy === t.key || !data.enabled}
                  data-testid={`subscribe-${t.key}`}
                  className="mt-4 bg-primary text-primary-foreground rounded-md py-2 text-xs font-semibold hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-1.5"
                  title={!data.enabled ? "Billing hasn't started yet" : undefined}
                >
                  {busy === t.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                  {data.enabled ? `Start ${data.trial_days}-day free trial` : "Coming soon"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-[11px] text-muted-foreground leading-relaxed border-t border-border pt-4">
        Every plan starts with a {data.trial_days}-day free trial. If a monthly charge fails, we give you a {data.grace_days}-day grace window to update your payment method before your shop is hidden from the buyer directory. Cancel any time — you keep access to the end of the current billing period.
      </p>
    </div>
  );
}

